import React from "react";
import { InsuranceCardProps } from "./Insurance.props";
import BookingGetters from "../../../store/onlineBooking/getters";
import { useTranslation } from "react-i18next";

function GretaInsuranceCardsLayout({
  insurances,
  onSelected,
  selectedInsuranceIndex = -1,
  insurancespec,
  selectedInsurance,
}: InsuranceCardProps) {
  const appSettings = BookingGetters.getAppSettings();
  const { t: translate } = useTranslation();

  const defaultCurrency = appSettings?.defaultCurrency;
  const showDailyPriceForInsuranceAndExtras =
    appSettings?.showDailyPriceForInsuranceAndExtras;
  const showDailyPriceOrNegotiableDailyPrice =
    appSettings?.showDailyPriceOrNegotiableDailyPrice;

  const bookingDetails = BookingGetters.getBookingDetails();
  const startDate = new Date(bookingDetails?.pickUpDateAndTime || Date.now());
  const endDate = new Date(bookingDetails?.dropOffDateAndTime || Date.now());
  const startDateUTC = new Date(
    Date.UTC(
      startDate.getFullYear(),
      startDate.getMonth(),
      startDate.getDate(),
      startDate.getHours()
    )
  );
  const endDateUTC = new Date(
    Date.UTC(
      endDate.getFullYear(),
      endDate.getMonth(),
      endDate.getDate(),
      endDate.getHours()
    )
  );
  const differenceInHour =
    (endDateUTC.getTime() - startDateUTC.getTime()) / (1000 * 60 * 60);
  const approximateDays = Math.max(1, Math.ceil(differenceInHour / 24));

  const isCoverageIncluded = (el: {
    isIncluded?: boolean;
    pricePerDayInUSD?: string;
  }) => el?.isIncluded ?? el?.pricePerDayInUSD === "0";

  const allInsurances = insurances
    .map((el, originalIndex) => {
      const availableInsurances: Record<string, boolean> = {};
      insurancespec.forEach((spec) => {
        const specFeature = spec.Description.toLowerCase().trim();
        const indicatorKey =
          spec.Description.toUpperCase() + ":" + el.name.toUpperCase();
        const indicator = el.indicator ? el.indicator[indicatorKey] : null;
        if (indicator) {
          availableInsurances[specFeature] = indicator.toLowerCase() === "tick";
        }
      });
      return { ...el, availableInsurances, originalIndex };
    })
    .sort((a, b) => {
      const aIncluded = isCoverageIncluded(a) ? 1 : 0;
      const bIncluded = isCoverageIncluded(b) ? 1 : 0;
      if (aIncluded !== bIncluded) return bIncluded - aIncluded;
      if (aIncluded === 1) return 0;
      return (
        parseFloat(a.pricePerDayInUSD || "0") -
        parseFloat(b.pricePerDayInUSD || "0")
      );
    });

  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: "24px",
        justifyContent: "center",
        width: "100%",
        alignItems: "stretch",
      }}
    >
      {allInsurances.map((el) => {
        const index = el.originalIndex;
        const isZeroPriced = el.isIncluded ?? el.pricePerDayInUSD === "0";
        const isSelected = selectedInsuranceIndex === index;
        const isHighlighted = isSelected || isZeroPriced;
        const stars: number | null = el.insuranceRating ?? null;
        const primaryColor = appSettings?.primaryColor || "#ff8f00";

        return (
          <div
            key={index}
            style={{
              flex: "1 1 280px",
              maxWidth: "340px",
              minWidth: "260px",
              borderRadius: "16px",
              border: el.isRecommended
                ? `2px solid ${primaryColor}`
                : isHighlighted
                ? "2px solid #111"
                : "1px solid #d0d0d0",
              backgroundColor: "#fff",
              boxShadow: isHighlighted
                ? "0 4px 20px rgba(0,0,0,0.15)"
                : "0 2px 8px rgba(0,0,0,0.08)",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              marginTop: "45px",
              marginBottom: "25px",
            }}
          >
            {/* Card Header */}
            <div
              style={{
                padding: "20px 20px 16px 20px",
                backgroundColor: "#E8E8E8",
                marginBottom: "12px",
              }}
            >
              <div
                style={{ display: "flex", flexDirection: "column", gap: "8px" }}
              >
                {el.isRecommended && (
                  <span
                    style={{
                      alignSelf: "flex-start",
                      border: `1px solid ${primaryColor}`,
                      color: primaryColor,
                      background: "#ffffff",
                      borderRadius: "9999px",
                      padding: "2px 12px",
                      fontSize: "11px",
                      fontWeight: 700,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {translate("Recommended")}
                  </span>
                )}
                {/* Row 1: Name + Circle */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                  }}
                >
                  <h3
                    style={{
                      fontWeight: "700",
                      fontSize: "18px",
                      color: "#111",
                      margin: 0,
                      lineHeight: "1.3",
                      flex: 1,
                      marginRight: "8px",
                    }}
                  >
                    {el.name}
                  </h3>
                  {/* Circle radio indicator */}
                  <div
                    onClick={() => {
                      if (!isZeroPriced) {
                        onSelected(isSelected ? -1 : index);
                      }
                    }}
                    style={{
                      width: "24px",
                      height: "24px",
                      borderRadius: "50%",
                      border: isHighlighted
                        ? "2px solid #111"
                        : "2px solid #aaa",
                      backgroundColor: isHighlighted ? "#111" : "#fff",
                      cursor: isZeroPriced ? "default" : "pointer",
                      flexShrink: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {isHighlighted && (
                      <div
                        style={{
                          width: "8px",
                          height: "8px",
                          borderRadius: "50%",
                          backgroundColor: "#fff",
                        }}
                      />
                    )}
                  </div>
                </div>
                {/* Row 2: Stars + Price on the same line */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginTop: "15px",
                  }}
                >
                  <div style={{ display: "flex", gap: "3px" }}>
                    {[1, 2, 3, 4, 5].map((s) => (
                      <span
                        key={s}
                        style={{
                          fontSize: "20px",
                          color: stars !== null && s <= stars ? "#111" : "#ccc",
                          lineHeight: "1",
                        }}
                      >
                        ★
                      </span>
                    ))}
                  </div>
                  {!isZeroPriced && (
                    <p
                      style={{
                        fontWeight: "700",
                        fontSize: "14px",
                        color: "#111",
                        margin: 0,
                        whiteSpace: "nowrap",
                        textAlign: "right",
                      }}
                    >
                      {defaultCurrency || "€"}{" "}
                      {showDailyPriceForInsuranceAndExtras ? (
                        <>
                          {(
                            parseFloat(el.pricePerDayInUSD) / approximateDays
                          ).toFixed(2)}
                          <span style={{ fontSize: "11px" }}>
                            {translate("/day")}
                          </span>
                        </>
                      ) : (showDailyPriceOrNegotiableDailyPrice &&
                          el.isDailyPrice) ||
                        el.isNegotiableDaily ? (
                        <>
                          {(
                            parseFloat(el.pricePerDayInUSD) / approximateDays
                          ).toFixed(2)}
                          <span style={{ fontSize: "11px" }}>
                            {translate("/day")}
                          </span>
                        </>
                      ) : (
                        el.pricePerDayInUSD
                      )}
                    </p>
                  )}
                </div>
                {el.isRecommended && (
                  <p
                    style={{
                      fontSize: "12px",
                      color: "#666666",
                      margin: 0,
                    }}
                  >
                    {translate("We recommend this coverage")}
                  </p>
                )}
              </div>
            </div>

            {/* Features list */}
            <div style={{ padding: "12px 20px", flex: 1 }}>
              {insurancespec.map((spec, i) => {
                const featureLower = spec.Description.toLowerCase().trim();
                const isAvailable =
                  el.availableInsurances?.[featureLower] ?? false;
                const bookingTextKey = `${spec.Description.toUpperCase()}:${el.name.toUpperCase()}`;
                const hasBookingText =
                  el.bookingText && el.bookingText[bookingTextKey];
                const displayText = spec.BookingText || spec.Description;

                return (
                  <div key={i} style={{ marginBottom: "10px" }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "8px",
                      }}
                    >
                      <span
                        style={{
                          color: isAvailable ? "#4caf50" : "#f44336",
                          fontSize: "17px",
                          lineHeight: "1.4",
                          flexShrink: 0,
                          fontWeight: "bold",
                        }}
                      >
                        {isAvailable ? "✓" : "✗"}
                      </span>
                      <div>
                        <span
                          style={{
                            fontWeight: "600",
                            fontSize: "13px",
                            color: "#111",
                          }}
                        >
                          {displayText}
                        </span>
                        {hasBookingText && (
                          <div
                            style={{
                              fontSize: "11px",
                              color: "#777",
                              marginTop: "2px",
                            }}
                            dangerouslySetInnerHTML={{
                              __html: el.bookingText[bookingTextKey],
                            }}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Bottom Button */}
            <div
              style={{
                padding: "16px 20px 24px 20px",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <button
                disabled={isZeroPriced}
                onClick={() => {
                  if (!isZeroPriced) {
                    onSelected(isSelected ? -1 : index);
                  }
                }}
                style={{
                  width: "45%",
                  padding: "14px 0",
                  borderRadius: `${appSettings?.buttonBorderRadius ?? 50}px`,
                  border: "none",
                  backgroundColor: isZeroPriced
                    ? "#bbb"
                    : isSelected
                    ? appSettings?.secondaryColor || "#ff8f00"
                    : appSettings?.primaryColor || "#ff8f00",
                  color: "#fff",

                  fontWeight: "600",
                  fontSize: "15px",
                  cursor: isZeroPriced ? "default" : "pointer",
                  letterSpacing: "0.3px",
                  textAlign: "center",
                }}
              >
                {isZeroPriced
                  ? translate("Included")
                  : isSelected
                  ? translate("Remove")
                  : translate("Select")}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export { GretaInsuranceCardsLayout };
